import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Server, Cloud, Check, AlertCircle } from 'lucide-react';
import { providers } from '../services/api';
import { useAuth } from '../context/AuthContext';
import ImageCatalogManager from '../components/ImageCatalogManager';

function Settings() {
  const { isAdmin } = useAuth();

  const {
    data: currentProvider,
    isLoading,
    isError: providerError,
    error: providerErrorObj,
  } = useQuery({
    queryKey: ['provider'],
    queryFn: () => providers.current(),
    retry: false,
  });

  const { data: availableProviders } = useQuery({
    queryKey: ['availableProviders'],
    queryFn: () => providers.available(),
    retry: false,
  });

  return (
    <div className="animate-fade-in" data-testid="settings-page">
      <div style={{ marginBottom: 32 }}>
        <h1 style={{ marginBottom: 8 }}>Settings</h1>
        <p className="text-muted">Platform configuration for Virtual Labs</p>
      </div>

      {/* Lab environments — the list faculty choose from. First, because it is the only
          thing on this page an administrator actually changes. */}
      {isAdmin && <ImageCatalogManager />}

      {/* Current Provider */}
      <div className="card" style={{ marginBottom: 24 }}>
        <div className="card-header">
          <h2 style={{ fontSize: 18, display: 'flex', alignItems: 'center', gap: 8 }}>
            <Server size={20} aria-hidden="true" />
            Container Provider
          </h2>
        </div>

        {isLoading ? (
          <div style={{ padding: 20, textAlign: 'center' }} role="status" aria-label="Loading provider info">
            <div className="spinner" style={{ margin: '0 auto' }} aria-hidden="true" />
          </div>
        ) : providerError ? (
          // This endpoint is known to fail on the Kubernetes provider. Say so plainly rather
          // than rendering an empty panel that reads as "nothing is configured".
          <div role="alert" style={{
            padding: '12px 16px', borderRadius: 8,
            background: 'var(--badge-warning-bg)', border: '1px solid var(--accent-warning)',
            color: 'var(--accent-warning)', fontSize: 13.5,
          }}>
            Provider details are unavailable
            {providerErrorObj?.response?.status ? ` (HTTP ${providerErrorObj.response.status})` : ''}.
            This panel is informational only — it does not affect running labs or lab creation.
          </div>
        ) : currentProvider ? (
          <div>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: 16,
              padding: 16,
              background: 'var(--bg-tertiary)',
              borderRadius: 8,
              marginBottom: 16,
            }}>
              <div style={{
                width: 48,
                height: 48,
                borderRadius: 12,
                background: currentProvider.status?.success
                  ? 'var(--badge-success-bg)'
                  : 'var(--badge-danger-bg)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                {currentProvider.status?.success ? (
                  <Check size={24} style={{ color: 'var(--accent-success)' }} aria-hidden="true" />
                ) : (
                  <AlertCircle size={24} style={{ color: 'var(--accent-danger)' }} aria-hidden="true" />
                )}
              </div>
              <div>
                <div style={{ fontWeight: 600, marginBottom: 4 }}>
                  {currentProvider.name === 'local' ? 'Local Docker' : currentProvider.name}
                </div>
                <div style={{ fontSize: 14, color: 'var(--text-secondary)' }}>
                  {currentProvider.status?.success ? 'Connected' : currentProvider.status?.message}
                </div>
              </div>
            </div>

            <div style={{ fontSize: 14 }}>
              <div style={{ marginBottom: 8 }}>
                <span style={{ color: 'var(--text-muted)' }}>Type: </span>
                <code>{currentProvider.type}</code>
              </div>
              <div style={{ marginBottom: 8 }}>
                <span style={{ color: 'var(--text-muted)' }}>Network: </span>
                <code>{currentProvider.config?.network || '—'}</code>
              </div>
              <div>
                <span style={{ color: 'var(--text-muted)' }}>Platform default image: </span>
                <code>{currentProvider.config?.image || '—'}</code>
                <p className="text-muted" style={{ fontSize: 12.5, marginTop: 4, marginBottom: 0 }}>
                  What a lab runs when it has chosen no environment of its own.
                </p>
              </div>
            </div>
          </div>
        ) : (
          <p className="text-muted">No provider configured</p>
        )}
      </div>

      {/* Available Providers */}
      <div className="card">
        <div className="card-header">
          <h2 style={{ fontSize: 18, display: 'flex', alignItems: 'center', gap: 8 }}>
            <Cloud size={20} aria-hidden="true" />
            Available Providers
          </h2>
        </div>

        <div style={{ display: 'grid', gap: 12 }}>
          {(availableProviders || []).map(provider => (
            <div
              key={provider.type}
              style={{
                padding: 16,
                background: 'var(--bg-tertiary)',
                borderRadius: 8,
                opacity: provider.disabled ? 0.5 : 1,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                <Server size={20} style={{ color: 'var(--accent-primary)' }} aria-hidden="true" />
                <div style={{ fontWeight: 600 }}>{provider.name}</div>
                {provider.disabled && (
                  <span className="badge badge-info">Coming Soon</span>
                )}
                {currentProvider?.name === provider.type && (
                  <span className="badge badge-success">Active</span>
                )}
              </div>
              <p style={{ fontSize: 14, color: 'var(--text-secondary)' }}>
                {provider.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default Settings;
